import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { approvalLinkService, getErrorMessage } from '../../services/apiService';
import DocumentViewer from './DocumentViewer';
import { fileKind, fmtSize, groupDocuments, initialChoice } from './documents';

/**
 * The page behind a one-time decision link: an approver without a VMS account (added by e-mail in
 * User Management) sees the one invite or supplier the e-mail was about and approves or rejects it.
 * No login; nothing else in VMS is reachable from here. The Approve / Reject buttons in the e-mail
 * open this page with that choice ready; it is only recorded once confirmed here.
 */

/** "manufacturing" → "Manufacturing" (values stored in lower case). */
const cap = (v) => (v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : v);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// The app clips html/body overflow (styles/tailwind.css), so a public page scrolls its own box.
const Shell = ({ children, wide = false }) => (
  <div className="h-screen overflow-y-auto bg-slate-50" data-testid="approval-page">
    <header className="bg-white border-b border-slate-200">
      <div className={`${wide ? 'max-w-6xl' : 'max-w-2xl'} mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4`}>
        <img src="/email/amber-logo.png" alt="Amber Enterprises India Limited" className="h-8 w-auto" />
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
          <Icon name="Lock" size={13} />
          Private approval link
        </span>
      </div>
    </header>
    <main className={`${wide ? 'max-w-6xl' : 'max-w-2xl'} mx-auto px-4 sm:px-6 py-6 sm:py-10`}>{children}</main>
  </div>
);

const Panel = ({ title, icon, aside, children, testid, bodyClass = 'p-5' }) => (
  <section className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid={testid}>
    {title && (
      <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          {icon && <Icon name={icon} size={16} className="text-slate-500" />}
          {title}
        </h2>
        {aside}
      </div>
    )}
    <div className={bodyClass}>{children}</div>
  </section>
);

/** Label/value rows; empty values are left out. */
const Facts = ({ rows }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-[minmax(0,11rem)_1fr] gap-x-4 gap-y-1 sm:gap-y-2.5 text-sm">
    {rows
      .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
      .map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-slate-500">{k}</dt>
          <dd className="text-slate-900 font-medium break-words mb-2 sm:mb-0">{v}</dd>
        </React.Fragment>
      ))}
  </dl>
);

const Message = ({ icon, tone = 'slate', title, children, testid }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-500',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    amber: 'bg-amber-100 text-amber-600',
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 sm:p-10 text-center" data-testid={testid} role="status">
      <span className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center ${tones[tone]}`}>
        <Icon name={icon} size={32} />
      </span>
      <h1 className="mt-4 text-xl font-semibold text-slate-900">{title}</h1>
      <div className="mt-2 text-sm text-slate-600 space-y-2">{children}</div>
    </div>
  );
};

const FILE_ICON = { pdf: ['FileText', 'bg-red-50 text-red-600'], image: ['FileImage', 'bg-sky-50 text-sky-600'], other: ['File', 'bg-slate-100 text-slate-500'] };

const DocumentList = ({ token, documents, withheld }) => {
  const [open, setOpen] = useState(null); // index into the flat list
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const groups = groupDocuments(documents);
  const flat = groups.flatMap((g) => g.docs);
  const download = useCallback(
    async (doc) => {
      try {
        setBusy(doc.id);
        setError(null);
        await approvalLinkService.download(token, doc.id, doc.file_name);
      } catch (e) {
        setError(getErrorMessage(e) || 'Could not download the document.');
      } finally {
        setBusy(null);
      }
    },
    [token]
  );
  const close = useCallback(() => setOpen(null), []);

  return (
    <>
      {!flat.length && <p className="text-sm text-slate-600">No documents were uploaded.</p>}
      {error && <p className="mb-3 text-sm text-red-600" role="alert">{error}</p>}
      <div className="space-y-5">
        {groups.map((g) => (
          <div key={g.key}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              {g.title} <span className="font-normal normal-case tracking-normal text-slate-400">· {g.docs.length}</span>
            </p>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 overflow-hidden">
              {g.docs.map((doc) => {
                const i = flat.indexOf(doc);
                const [icon, cls] = FILE_ICON[fileKind(doc)];
                return (
                  <li key={doc.id} className="flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-slate-50" data-testid={`document-${doc.id}`}>
                    <span className={`h-9 w-9 flex-shrink-0 rounded-lg flex items-center justify-center ${cls}`} aria-hidden="true">
                      <Icon name={icon} size={18} />
                    </span>
                    <button type="button" onClick={() => setOpen(i)} className="min-w-0 flex-1 text-left group focus-visible:outline-none">
                      <span className="block text-sm font-medium text-slate-900 truncate group-hover:text-primary group-focus-visible:underline">{doc.type_label || doc.file_name}</span>
                      <span className="block text-xs text-slate-500 truncate">
                        {[doc.file_name, fmtSize(doc.file_size), doc.uploaded_at ? fmtDay(doc.uploaded_at) : null].filter(Boolean).join(' · ')}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpen(i)}
                      className="inline-flex items-center gap-1.5 h-8 px-2.5 sm:px-3 rounded-md border border-slate-300 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary flex-shrink-0"
                      aria-label={`View ${doc.type_label || doc.file_name}`}
                      data-testid={`document-view-${doc.id}`}
                    >
                      <Icon name="Eye" size={14} />
                      <span className="hidden sm:inline">View</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => download(doc)}
                      disabled={busy === doc.id}
                      className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      aria-label={`Download ${doc.type_label || doc.file_name}`}
                      title="Download"
                    >
                      <Icon name={busy === doc.id ? 'Loader2' : 'Download'} size={16} className={busy === doc.id ? 'animate-spin' : ''} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      {withheld > 0 && (
        <p className="mt-4 flex items-start gap-2 text-xs text-slate-500">
          <Icon name="EyeOff" size={14} className="flex-shrink-0 mt-px" />
          Bank details, the cancelled cheque and the authorised signature are not shown through this link.
        </p>
      )}
      {open !== null && flat[open] && <DocumentViewer token={token} docs={flat} index={open} onIndex={setOpen} onClose={close} onDownload={download} />}
    </>
  );
};

/** Choose approve or reject (the e-mail may have chosen already), say why if rejecting, confirm. */
const DecisionBox = React.forwardRef(({ isInvite, email, expiresAt, choice, onChoice, onDecide }, ref) => {
  const [comments, setComments] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const reasonRef = useRef(null);

  useEffect(() => {
    setError(null);
    if (choice === 'REJECTED') reasonRef.current?.focus({ preventScroll: true });
  }, [choice]);

  const options = [
    {
      value: 'APPROVED',
      label: isInvite ? 'Approve' : 'Quality approved',
      hint: isInvite ? 'The vendor is e-mailed the registration link straight away.' : 'The supplier moves on to Level 1 approval.',
      icon: 'CheckCircle2',
      on: 'border-green-600 bg-green-50 ring-1 ring-green-600',
      iconOn: 'text-green-600',
    },
    {
      value: 'REJECTED',
      label: 'Reject',
      hint: isInvite ? 'The invite is closed and the vendor is not contacted.' : 'The supplier is rejected; the team sees your reason.',
      icon: 'XCircle',
      on: 'border-red-600 bg-red-50 ring-1 ring-red-600',
      iconOn: 'text-red-600',
    },
  ];

  const submit = async () => {
    if (!choice) return setError('Choose approve or reject.');
    if (choice === 'REJECTED' && !comments.trim()) {
      setError('Please write the reason for rejecting.');
      reasonRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onDecide(choice, comments.trim());
    } catch (e) {
      setError(getErrorMessage(e));
      setSaving(false);
    }
  };

  const confirmLabel = !choice
    ? 'Confirm decision'
    : choice === 'APPROVED'
      ? isInvite
        ? 'Approve and send the link'
        : 'Confirm quality approval'
      : isInvite
        ? 'Reject this invite'
        : 'Reject this supplier';

  return (
    <section ref={ref} className="bg-white rounded-xl border border-slate-200 shadow-sm scroll-mt-6" data-testid="decision-box" aria-labelledby="decision-title">
      <div className="px-5 pt-4 pb-3 border-b border-slate-100">
        <h2 id="decision-title" className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Icon name="Gavel" size={16} className="text-slate-500" />
          Your decision
        </h2>
      </div>
      <div className="p-5 space-y-4">
        <div role="radiogroup" aria-label="Decision" className="grid grid-cols-1 gap-2">
          {options.map((o) => {
            const on = choice === o.value;
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onChoice(o.value)}
                disabled={saving}
                className={`w-full text-left rounded-lg border px-3.5 py-3 flex items-start gap-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  on ? o.on : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
                data-testid={`choice-${o.value.toLowerCase()}`}
              >
                <Icon name={o.icon} size={20} className={`flex-shrink-0 mt-px ${on ? o.iconOn : 'text-slate-400'}`} />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">{o.label}</span>
                  <span className="block text-xs text-slate-600 mt-0.5">{o.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">
            {choice === 'REJECTED' ? (
              <>
                Reason for rejecting <span className="text-red-600">*</span>
              </>
            ) : (
              <>
                Comments <span className="font-normal text-slate-500">(optional)</span>
              </>
            )}
          </span>
          <textarea
            ref={reasonRef}
            value={comments}
            onChange={(e) => {
              setComments(e.target.value);
              setError(null);
            }}
            rows={3}
            maxLength={2000}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={choice === 'REJECTED' ? 'The colleague who raised it will see this' : 'Anything the team should know'}
            disabled={saving}
            data-testid="decision-comments"
          />
        </label>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <Button
          className="w-full"
          variant={choice === 'REJECTED' ? 'destructive' : 'default'}
          onClick={submit}
          disabled={saving || !choice}
          iconName={saving ? 'Loader2' : choice === 'REJECTED' ? 'X' : 'Check'}
          iconPosition="left"
          data-testid="decision-confirm"
        >
          {saving ? 'Saving…' : confirmLabel}
        </Button>
        <div className="text-xs text-slate-500 leading-relaxed border-t border-slate-100 pt-3 space-y-0.5">
          <p>
            You are deciding as <span className="block font-medium text-slate-700 [overflow-wrap:anywhere]">{email}</span>
          </p>
          <p>This cannot be undone. The link works once{expiresAt ? `, until ${fmtDate(expiresAt)}` : ''}.</p>
        </div>
      </div>
    </section>
  );
});
DecisionBox.displayName = 'DecisionBox';

const Chip = ({ icon, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700">
    <Icon name={icon} size={12} className="text-slate-400" />
    {children}
  </span>
);

const ApprovalDecisionPage = () => {
  const [params] = useSearchParams();
  const location = useLocation();
  const token = params.get('token') || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(null); // 'APPROVED' | 'REJECTED'
  const [renew, setRenew] = useState(null); // { sending } | result
  const [choice, setChoice] = useState(() => initialChoice(location.search));
  const [boxInView, setBoxInView] = useState(false);
  const decisionRef = useRef(null);

  // The token is the only credential: keep it out of referrers and search engines.
  useEffect(() => {
    const metas = [
      ['referrer', 'no-referrer'],
      ['robots', 'noindex, nofollow'],
    ].map(([name, content]) => {
      const m = document.createElement('meta');
      m.name = name;
      m.content = content;
      document.head.appendChild(m);
      return m;
    });
    document.title = 'Approval · Amber Enterprises';
    return () => metas.forEach((m) => m.remove());
  }, []);

  useEffect(() => {
    if (!token) {
      setError('This link is not complete. Please use the button in your e-mail.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await approvalLinkService.view(token);
        setData(res.data);
      } catch (e) {
        setError(getErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // On a phone the decision sits below the details; a bar at the bottom reaches it until it is on screen.
  useEffect(() => {
    const el = decisionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => setBoxInView(entry.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [data]);

  const goToDecision = (value) => {
    setChoice(value);
    decisionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const decide = async (decision, comments) => {
    await approvalLinkService.decide(token, decision, comments);
    setDone(decision);
  };

  const askForNewLink = async () => {
    setRenew({ sending: true });
    try {
      const res = await approvalLinkService.renew(token);
      setRenew(res.data);
    } catch (e) {
      setRenew({ error: getErrorMessage(e) });
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="py-20 text-center text-slate-500">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-500 mx-auto" />
          <p className="mt-3 text-sm">Loading…</p>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <Message icon="Link2Off" tone="red" title="This link cannot be opened" testid="link-error">
          <p>{error}</p>
          <p className="text-xs text-slate-500">If you think this is a mistake, reply to the e-mail you received.</p>
        </Message>
      </Shell>
    );
  }

  const isInvite = data.kind === 'INVITE';
  const what = isInvite ? 'vendor invite' : 'supplier';

  if (done) {
    return (
      <Shell>
        <Message
          icon={done === 'APPROVED' ? 'CheckCircle2' : 'XCircle'}
          tone={done === 'APPROVED' ? 'green' : 'red'}
          title={done === 'APPROVED' ? 'Approved — thank you' : 'Rejected'}
          testid="decision-done"
        >
          <p className="font-medium text-slate-800">{data.title}</p>
          <p>
            {done === 'APPROVED'
              ? isInvite
                ? 'The vendor is being sent the registration link now, and the colleague who raised the invite is told.'
                : 'The supplier moves on to Level 1 approval, and the team is told.'
              : isInvite
                ? 'The invite is closed; the vendor is not contacted. The colleague who raised it sees your reason.'
                : 'The supplier has been rejected; the team sees your reason.'}
          </p>
          <p className="text-xs text-slate-500">You can close this page. This link cannot be used again.</p>
        </Message>
      </Shell>
    );
  }

  if (data.state === 'decided') {
    const d = data.decided || {};
    const outcome = d.decision === 'APPROVED' ? 'approved' : d.decision === 'REJECTED' ? 'rejected' : 'decided';
    return (
      <Shell>
        <Message icon={d.decision === 'REJECTED' ? 'XCircle' : 'CheckCircle2'} tone={d.decision === 'REJECTED' ? 'red' : 'green'} title={`This ${what} has already been ${outcome}`} testid="link-decided">
          <p className="font-medium text-slate-800">{data.title}</p>
          {d.by && (
            <p>
              By {d.by}
              {d.at ? ` on ${fmtDate(d.at)}` : ''}.
            </p>
          )}
          {d.comments && <p className="italic">“{d.comments}”</p>}
          <p className="text-xs text-slate-500">Nothing more is needed from you.</p>
        </Message>
      </Shell>
    );
  }

  if (data.state === 'expired') {
    return (
      <Shell>
        <Message icon="Clock" tone="amber" title="This link has expired" testid="link-expired">
          <p className="font-medium text-slate-800">{data.title}</p>
          <p>
            The {what} is still waiting for a decision. A new link can be sent to <span className="font-medium">{data.email}</span>.
          </p>
          {renew?.sent ? (
            <p className="text-green-700 font-medium" data-testid="renew-sent">
              A new link has been sent to {data.email}. Please use the latest e-mail.
            </p>
          ) : renew?.reason === 'already_sent' ? (
            <p className="text-slate-700" data-testid="renew-sent">
              A newer link was already sent to {data.email}. Please use the latest e-mail.
            </p>
          ) : renew?.error ? (
            <p className="text-red-600" role="alert">
              {renew.error}
            </p>
          ) : null}
          {!renew?.sent && renew?.reason !== 'already_sent' && (
            <div className="pt-2">
              <Button onClick={askForNewLink} disabled={renew?.sending} iconName={renew?.sending ? 'Loader2' : 'Send'} iconPosition="left">
                Send me a new link
              </Button>
            </div>
          )}
        </Message>
      </Shell>
    );
  }

  // Pending: the details on the left, the decision beside them (below them on a phone).
  const s = data.supplier;
  const inv = data.invite;
  const nature = isInvite ? inv.nature_of_business : s.nature_of_business;
  const sub = isInvite ? inv.supplier_subcategory : s.supplier_subcategory;
  return (
    <Shell wide>
      <div className="pb-24 lg:pb-0">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">{isInvite ? 'Approval needed · Vendor invite' : 'Approval needed · Quality'}</p>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900 break-words">{data.title}</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            {isInvite
              ? 'A colleague wants to invite this vendor to register. The registration link goes to the vendor only after the invite is approved.'
              : 'This material supplier has completed registration and is on hold until quality approves it; after that it goes for Level 1 approval.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Chip icon="Tags">{[nature, sub].filter(Boolean).join(' · ')}</Chip>
            {isInvite ? (
              <Chip icon="User">Raised by {inv.raised_by || 'a colleague'}{inv.raised_at ? ` · ${fmtDay(inv.raised_at)}` : ''}</Chip>
            ) : (
              <Chip icon="CalendarCheck">Registered {fmtDay(s.submitted_at)}</Chip>
            )}
            {!isInvite && <Chip icon="Paperclip">{(data.documents || []).length} documents</Chip>}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] gap-5 lg:gap-6 items-start">
          <div className="space-y-5 min-w-0">
            {isInvite ? (
              <>
                <Panel title="Vendor" icon="Building2" testid="invite-details">
                  <Facts
                    rows={[
                      ['Vendor', inv.vendor_name],
                      ['Vendor e-mail', inv.vendor_email],
                      ['Nature of business', inv.nature_of_business],
                      ['Supplier type', inv.oem_odm],
                      ['Sub-category', inv.supplier_subcategory],
                    ]}
                  />
                </Panel>
                <Panel title="Raised by" icon="User">
                  <Facts
                    rows={[
                      ['Name', inv.raised_by],
                      ['E-mail', inv.raised_by_email && inv.raised_by_email !== inv.raised_by ? inv.raised_by_email : null],
                      ['Raised on', fmtDate(inv.raised_at)],
                    ]}
                  />
                </Panel>
                <Panel title="What happens next" icon="ListChecks">
                  <ol className="space-y-3 text-sm text-slate-700">
                    {[
                      ['You approve', 'The vendor is e-mailed a registration link right away (valid for 7 days).'],
                      ['The vendor registers', 'They fill in the registration form and upload their documents.'],
                      ['Approvals continue in VMS', 'Material (RM / PM) suppliers go to quality first, then Level 1.'],
                    ].map(([t, d], i) => (
                      <li key={t} className="flex gap-3">
                        <span className="h-6 w-6 flex-shrink-0 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                        <span>
                          <span className="font-medium text-slate-900">{t}.</span> {d}
                        </span>
                      </li>
                    ))}
                  </ol>
                </Panel>
              </>
            ) : (
              <>
                <Panel title="Company" icon="Building2" testid="supplier-company">
                  <Facts
                    rows={[
                      ['Company', s.company_name],
                      ['Constitution', s.constitution_of_business],
                      ['GSTIN', s.gstin],
                      ['PAN', s.pan],
                      ['CIN', s.cin_number],
                      ['Year established', s.year_established],
                      ['Website', s.website],
                      ['Registration submitted', fmtDate(s.submitted_at)],
                    ]}
                  />
                </Panel>
                <Panel title="Documents" icon="Paperclip" testid="supplier-documents" aside={<span className="text-xs text-slate-500">Click a document to view it here</span>}>
                  <DocumentList token={token} documents={data.documents} withheld={data.withheld_documents || 0} />
                </Panel>
                <Panel title="Classification" icon="Tags">
                  <Facts
                    rows={[
                      ['Nature of business', s.nature_of_business],
                      ['Supplier type', s.oem_odm],
                      ['Sub-category', s.supplier_subcategory],
                      ['Vendor type', cap(s.vendor_type)],
                      [
                        'MSME',
                        [
                          s.msme_status === 'MSME' ? 'Registered' : s.msme_status === 'NON_MSME' ? 'Not registered' : cap(s.msme_status?.toLowerCase()),
                          cap(s.msme_category),
                        ]
                          .filter(Boolean)
                          .join(' · '),
                      ],
                      ['Udyam number', s.msme_number],
                      ['Classification year', s.classification_year],
                      ['Annual turnover', s.annual_turnover ? `${Number(s.annual_turnover).toLocaleString('en-IN')} ${s.currency || ''}`.trim() : null],
                      ['Employees', s.employee_count],
                    ]}
                  />
                  {s.business_description && <p className="mt-4 text-sm text-slate-700 whitespace-pre-line border-t border-slate-100 pt-3">{s.business_description}</p>}
                </Panel>
                <Panel title="Contact and addresses" icon="MapPin">
                  <Facts
                    rows={[
                      ['Contact person', [s.contact_person_name, s.designation].filter(Boolean).join(', ')],
                      ['E-mail', s.email],
                      ['Phone', s.phone_number],
                      ['Director', s.director_name],
                      ['Registered address', s.registered_address],
                      ['Supply address', s.supply_address],
                    ]}
                  />
                </Panel>
              </>
            )}
          </div>

          <aside className="lg:sticky lg:top-6">
            <DecisionBox ref={decisionRef} isInvite={isInvite} email={data.email} expiresAt={data.expires_at} choice={choice} onChoice={setChoice} onDecide={decide} />
          </aside>
        </div>
      </div>

      {!boxInView && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3 flex gap-2 shadow-[0_-4px_16px_rgba(15,23,42,0.08)]" data-testid="decision-bar">
          <Button variant="outline" className="flex-1 border-red-300 text-red-700" onClick={() => goToDecision('REJECTED')} iconName="X" iconPosition="left">
            Reject
          </Button>
          <Button className="flex-1" onClick={() => goToDecision('APPROVED')} iconName="Check" iconPosition="left">
            Approve
          </Button>
        </div>
      )}
    </Shell>
  );
};

export default ApprovalDecisionPage;
